/** @format */
/**
 * Single source of truth for the storefront's URL shapes (bead outstanding-qbo.6).
 *
 * Before this module the same paths were maintained by hand in five places —
 * the router (src/main.ts), the crawler-prerender file paths (functions/),
 * the sitemap's STATIC_PATHS + URL templates (functions/sitemap.xml.ts), the
 * prerender canonicals, and the API's Stripe success/cancel redirect URLs
 * (apps/api/src/routes/cart.ts) — so a route rename could silently 404 the
 * sitemap or, worst case, send a PAYING customer returning from Stripe to
 * NotFound. Change a shape HERE and let the consumers + src/lib/paths.spec.ts
 * keep everything honest.
 *
 * NOTE: this module must stay dependency-free (no Vue/router imports): it is
 * bundled both by Vite (SPA) and by the Cloudflare Pages Functions build via a
 * relative import from functions/.
 */

/**
 * The router's path patterns (vue-router syntax), keyed by route name.
 * src/main.ts consumes these verbatim for its `routes` array — it must not
 * carry its own path literals (asserted by src/lib/paths.spec.ts).
 */
export const ROUTE_PATTERNS = {
  home: "/",
  collection: "/collection/:collectionSlug",
  detail: "/collection/:collectionSlug/:photoSlug",
  detail2: "/photos/photo/:photoSlug",
  cart: "/cart",
  favorites: "/favorites",
  success: "/cart/:orderId/success",
  failure: "/cart/:orderId/failure",
  // Token-gated customer order-status page (bead d5b). NOT in STATIC_PATHS — it's
  // per-order and reached only via the ?token= link in order emails, never crawled.
  orderStatus: "/order/:orderId",
  support: "/support",
  about: "/about",
  stories: "/stories",
  story: "/stories/:storySlug",
  faqs: "/frequently-asked-questions",
  refunds: "/refunds",
  privacy: "/privacy",
  terms: "/terms",
  NotFound: "/:pathMatch(.*)*",
  Error: "/error",
} as const;

/**
 * Static (param-less) marketing pages listed in the dynamic sitemap
 * (functions/sitemap.xml.ts). A subset of the router's paths by construction —
 * every entry references ROUTE_PATTERNS, and the spec asserts the subset
 * relation stays true.
 */
export const STATIC_PATHS: readonly string[] = [
  ROUTE_PATTERNS.home,
  ROUTE_PATTERNS.about,
  ROUTE_PATTERNS.stories,
  ROUTE_PATTERNS.faqs,
  ROUTE_PATTERNS.support,
  ROUTE_PATTERNS.refunds,
  ROUTE_PATTERNS.privacy,
  ROUTE_PATTERNS.terms,
];

/* Concrete-path builders. Each must render its ROUTE_PATTERNS shape with the
 * params substituted (asserted by the spec). Callers append their own origin
 * and query string. */

/** Canonical product (photo detail) path — the sitemap's flat shape, and the
 * target of the nested collection-photo prerender's rel=canonical. */
export const productPath = (photoSlug: string): string => `/photos/photo/${photoSlug}`;

/** Collection listing path. */
export const collectionPath = (collectionSlug: string): string =>
  `/collection/${collectionSlug}`;

/** Editorial story path. */
export const storyPath = (storySlug: string): string => `/stories/${storySlug}`;
